// Utility to fetch and set window.cryptoTable
export interface RuntimeCryptoPrice {
  symbol: string;
  priceUsd: number;
  marketCapRank?: number;
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const shouldReplacePrice = (current: RuntimeCryptoPrice | undefined, candidate: RuntimeCryptoPrice): boolean => {
  if (!current) {
    return true;
  }

  const currentPositive = isFiniteNumber(current.priceUsd) && current.priceUsd > 0;
  const candidatePositive = isFiniteNumber(candidate.priceUsd) && candidate.priceUsd > 0;

  if (candidatePositive !== currentPositive) {
    return candidatePositive;
  }

  const currentRank = current.marketCapRank;
  const candidateRank = candidate.marketCapRank;
  if (typeof currentRank === 'number' && typeof candidateRank === 'number' && currentRank !== candidateRank) {
    return candidateRank < currentRank;
  }
  if (typeof currentRank !== 'number' && typeof candidateRank === 'number') {
    return true;
  }

  return false;
};

export async function fetchAndSetCryptoTable(): Promise<RuntimeCryptoPrice[]> {
  const res = await fetch('/api/cryptos/all');
  const data = await res.json();
  const rawEntries = Array.isArray(data)
    ? data
    : Array.isArray(data.content)
      ? data.content
      : [];

  const bestBySymbol = new Map<string, RuntimeCryptoPrice>();
  rawEntries.forEach((c: { symbol: string; currentPrice: number; marketCapRank?: number }) => {
    const symbol = typeof c?.symbol === 'string' ? c.symbol.trim().toUpperCase() : '';
    if (!symbol) {
      return;
    }

    const candidate: RuntimeCryptoPrice = {
      symbol,
      priceUsd: isFiniteNumber(c?.currentPrice) ? c.currentPrice : 0,
      ...(typeof c?.marketCapRank === 'number' ? { marketCapRank: c.marketCapRank } : {}),
    };

    if (shouldReplacePrice(bestBySymbol.get(symbol), candidate)) {
      bestBySymbol.set(symbol, candidate);
    }
  });

  const cryptoTable = Array.from(bestBySymbol.values()).map(({ symbol, priceUsd, marketCapRank }) => ({
    symbol,
    priceUsd,
    ...(typeof marketCapRank === 'number' ? { marketCapRank } : {}),
  }));

  window.cryptoTable = cryptoTable;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('ecotoneai_cryptoTable_updated'));
  }
  // Optionally cache in localStorage
  localStorage.setItem('ecotoneai_cryptoTable', JSON.stringify(cryptoTable));
  return cryptoTable;
}
